<template>
  <div class="w-full">
    <portal :to="type + '-stats-' + componentId">
      <div class="w-1/6 | table-left-cell">
        {{ type === "display" ? "Display" : "Video" }}
      </div>
      <div class="w-1/6 | table-cell numeric text-right">
        {{ budgetRemainingPerDay.total | money }}
      </div>
      <div class="w-1/6 | table-cell numeric text-right">
        {{ budgetRemaining.total | money }}
      </div>
      <div
        class="w-1/6 | table-cell numeric text-right"
        :class="budgetHighlight(percentBudgetOverUnder.total)"
      >
        {{ percentBudgetOverUnder.total | percent }}
      </div>
      <div
        class="w-1/6 | table-cell numeric text-right"
        :class="impressionsHighlight(percentImpressionsOverUnder.total)"
      >
        {{ percentImpressionsOverUnder.total | percent }}
      </div>
      <div
        class="w-1/6 | table-cell numeric text-right"
        :class="mediaHighlight(percentForMediaOverUnder.total)"
      >
        {{ percentForMediaOverUnder.total | percent }}
      </div>

      <slide-up-down
        class="w-full flex flex-wrap"
        :active="expanded"
        :duration="500"
      >
        <div
          class="w-full flex flex-wrap"
          v-for="service in data[type].services"
          :key="service + '-1'"
        >
          <div class="w-1/6 | table-left-cell indent">
            {{ serviceNames[service] }}
          </div>
          <div class="w-1/6 | table-cell numeric text-right highlight">
            {{ budgetRemainingPerDay[service] | money }}
          </div>
          <div class="w-1/6 | table-cell numeric text-right highlight">
            {{ budgetRemaining[service] | money }}
          </div>
          <div
            class="w-1/6 | table-cell numeric text-right highlight"
            :class="budgetHighlight(percentBudgetOverUnder.total)"
          >
            {{ percentBudgetOverUnder[service] | percent }}
          </div>
          <div
            class="w-1/6 | table-cell numeric text-right highlight"
            :class="impressionsHighlight(percentImpressionsOverUnder.total)"
          >
            {{ percentImpressionsOverUnder[service] | percent }}
          </div>
          <div
            class="w-1/6 | table-cell numeric text-right highlight"
            :class="mediaHighlight(percentForMediaOverUnder.total)"
          >
            {{ percentForMediaOverUnder[service] | percent }}
          </div>
        </div>
      </slide-up-down>
    </portal>

    <slide-up-down
      class="w-full flex flex-wrap"
      :class="{ 'mb-2': expanded }"
      :active="expanded"
      :duration="500"
    >
      <hr class="ruler" />
    </slide-up-down>

    <slide-up-down
      class="w-full flex flex-wrap"
      :class="{ 'mb-4': expanded }"
      :active="expanded"
      :duration="500"
    >
      <div class="w-full text-center | heading">
        {{ type === "display" ? "Display" : "Video" }}
      </div>

      <div class="w-1/2 text-center | label">CPM</div>
      <div class="w-1/2 text-center | label">% for Media</div>

      <div class="w-1/2 text-center border-r border-gray-800 | figure numeric">
        {{ data[type].cpm | money }}
      </div>
      <div class="w-1/2 text-center | figure numeric">
        {{ (data[type].percent / 100) | percent }}
      </div>

      <div class="w-full flex flex-wrap mt-4">
        <div class="w-1/4">&nbsp;</div>
        <div class="w-1/4 | table-heading-t">Budget</div>
        <div class="w-1/4 | table-heading-t">Actual Spend</div>
        <div class="w-1/4 | table-heading-t">Actual Imp.</div>

        <div class="w-1/4 | table-left-cell indent">Total</div>
        <div class="w-1/4 | table-cell numeric text-right">
          {{ sum(data[type].budgets) | money }}
        </div>
        <div class="w-1/4 | table-cell numeric text-right">
          {{ actualBudget.total | money }}
        </div>
        <div class="w-1/4 | table-cell numeric text-right">
          {{ actualImpressions.total | number }}
        </div>

        <div
          class="w-full flex flex-wrap"
          v-for="service in data[type].services"
          :key="service + '-2'"
        >
          <div class="w-1/4 | table-left-cell indent">
            {{ serviceNames[service] }}
          </div>
          <div class="w-1/4 | table-cell numeric text-right">
            {{ data[type].budgets[service] | money }}
          </div>
          <div class="w-1/4 | table-cell numeric text-right">
            {{ actualBudget[service] | money }}
          </div>
          <div class="w-1/4 | table-cell numeric text-right">
            {{ actualImpressions[service] | number }}
          </div>
        </div>
      </div>
    </slide-up-down>

    <slide-up-down
      class="w-full flex flex-wrap"
      :class="{ 'mb-4': expanded }"
      :active="expanded"
      :duration="500"
    >
      <div class="w-full flex flex-wrap mt-4">
        <div class="w-1/4">&nbsp;</div>
        <div class="w-1/4 | table-heading-t">Target Budget</div>
        <div class="w-1/4 | table-heading-t">.. To Date</div>
        <div class="w-1/4 | table-heading-t">Proj. Budget</div>

        <div class="w-1/4 | table-left-cell indent">Total</div>
        <div class="w-1/4 | table-cell numeric text-right">
          {{ targetBudget.total | money }}
        </div>
        <div class="w-1/4 | table-cell numeric text-right">
          {{ targetBudgetToDate.total | money }}
        </div>
        <div class="w-1/4 | table-cell numeric text-right">
          {{ projectedBudget.total | money }}
        </div>

        <div
          class="w-full flex flex-wrap"
          v-for="service in data[type].services"
          :key="service + '-2'"
        >
          <div class="w-1/4 | table-left-cell indent">
            {{ serviceNames[service] }}
          </div>
          <div class="w-1/4 | table-cell numeric text-right">
            {{ targetBudget[service] | money }}
          </div>
          <div class="w-1/4 | table-cell numeric text-right">
            {{ targetBudgetToDate[service] | money }}
          </div>
          <div class="w-1/4 | table-cell numeric text-right">
            {{ projectedBudget[service] | money }}
          </div>
        </div>
      </div>
    </slide-up-down>

    <slide-up-down
      class="w-full flex flex-wrap"
      :class="{ 'mb-4': expanded }"
      :active="expanded"
      :duration="500"
    >
      <div class="w-full flex flex-wrap mt-4">
        <div class="w-1/6">&nbsp;</div>
        <div class="w-1/6 | table-heading-t">Target Imp.</div>
        <div class="w-1/6 | table-heading-t">.. To Date</div>
        <div class="w-1/6 | table-heading-t">.. Spent</div>
        <div class="w-1/6 | table-heading-t">.. Spent O/U</div>
        <div class="w-1/6 | table-heading-t">Proj. Imp.</div>

        <div class="w-1/6 | table-left-cell indent">Total</div>
        <div class="w-1/6 | table-cell numeric text-right">
          {{ targetImpressions.total | number }}
        </div>
        <div class="w-1/6 | table-cell numeric text-right">
          {{ targetImpressionToDate.total | number }}
        </div>
        <div class="w-1/6 | table-cell numeric text-right">
          {{ targetImpressionsSpent.total | number }}
        </div>
        <div class="w-1/6 | table-cell numeric text-right">
          {{ targetImpressionsSpentOverUnder.total | number }}
        </div>
        <div class="w-1/6 | table-cell numeric text-right">
          {{ projectedImpressions.total | number }}
        </div>

        <div
          class="w-full flex flex-wrap"
          v-for="service in data[type].services"
          :key="service + '-3'"
        >
          <div class="w-1/6 | table-left-cell indent">
            {{ serviceNames[service] }}
          </div>
          <div class="w-1/6 | table-cell numeric text-right">
            {{ targetImpressions[service] | number }}
          </div>
          <div class="w-1/6 | table-cell numeric text-right">
            {{ targetImpressionToDate[service] | number }}
          </div>
          <div class="w-1/6 | table-cell numeric text-right">
            {{ targetImpressionsSpent[service] | number }}
          </div>
          <div class="w-1/6 | table-cell numeric text-right">
            {{ targetImpressionsSpentOverUnder[service] | number }}
          </div>
          <div class="w-1/6 | table-cell numeric text-right">
            {{ projectedImpressions[service] | number }}
          </div>
        </div>
      </div>
    </slide-up-down>
  </div>
</template>

<script>
import HighlightMixin from "./HighlightMixin"
import { each } from "lodash"

export default {
  name: "BudgetCPMItem",
  mixins: [HighlightMixin],
  props: {
    data: {
      required: true,
    },
    expanded: {
      required: true,
    },
    daysInPeriod: {
      required: true,
    },
    elapsedDaysInPeriod: {
      required: true,
    },
    componentId: {
      required: true,
    },
    type: {
      required: true,
    },
  },
  data() {
    return {
      higlights: {
        green: "green",
        yellow: "yellow",
        red: "red",
      },
      budgetKeys: {
        display: {
          google: "google_display",
          google_remarketing: "google_display_remarketing",
          facebook: "facebook_display",
          facebook_remarketing: "facebook_display_remarketing",
          simplifi: "simplifi_display",
        },
        video: {
          google: "google_video",
          google_remarketing: "google_video_remarketing",
          facebook: "facebook_video",
          facebook_remarketing: "facebook_video_remarketing",
          simplifi: "simplifi_tv",
          tremor: "tremor_tv",
        },
      },
      serviceNames: {
        google_display: "Google",
        google_display_remarketing: "Google RM",
        facebook_display: "Facebook",
        facebook_display_remarketing: "Facebook RM",
        google_video: "Google",
        google_video_remarketing: "Google RM",
        facebook_video: "Facebook",
        facebook_video_remarketing: "FB RM",
        simplifi_display: "Simpli.fi",
        simplifi_tv: "Simpli.fi",
        tremor_tv: "Tremor",
      },
    }
  },
  computed: {
    actualBudget() {
      let data = {
        total: 0,
      }
      each(this.budgetKeys[this.type], (item, key) => {
        data[item] = this.data.stats[this.type][key].cost
      })

      data.total = this.sum(data)

      return data
    },
    actualImpressions() {
      let data = {
        total: 0,
      }
      each(this.budgetKeys[this.type], (item, key) => {
        data[item] = this.data.stats[this.type][key].impressions
      })

      data.total = this.sum(data)

      return data
    },
    targetBudget() {
      let data = {
        total: 0,
      }
      each(this.budgetKeys[this.type], (item) => {
        data[item] =
          this.data[this.type].budgets[item] *
          (this.data[this.type].percent / 100)
      })

      data.total = this.sum(data)

      return data
    },
    targetBudgetToDate() {
      let data = {
        total: 0,
      }
      each(this.budgetKeys[this.type], (item) => {
        data[item] =
          (this.targetBudget[item] / this.daysInPeriod) *
          this.elapsedDaysInPeriod
      })

      data.total = this.sum(data)
      return data
    },
    projectedBudget() {
      let data = {
        total: 0,
      }
      each(this.budgetKeys[this.type], (item, key) => {
        data[item] =
          (this.data.stats[this.type][key].cost / this.elapsedDaysInPeriod) *
          this.daysInPeriod
      })

      data.total = this.sum(data)

      return data
    },
    budgetRemaining() {
      let data = {
        total: 0,
      }
      each(this.budgetKeys[this.type], (item, key) => {
        data[item] =
          this.targetBudget[item] - this.data.stats[this.type][key].cost
      })

      data.total = this.sum(data)
      return data
    },
    budgetRemainingPerDay() {
      let divisor = this.daysInPeriod - this.elapsedDaysInPeriod,
        data = {
          total: 0,
        }
      each(this.budgetKeys[this.type], (item) => {
        data[item] =
          divisor > 0
            ? this.budgetRemaining[item] / divisor
            : this.budgetRemaining[item]
      })

      data.total = this.sum(data)
      return data
    },
    percentBudgetOverUnder() {
      let data = {
        total: 0,
      }
      each(this.budgetKeys[this.type], (item, key) => {
        data[item] =
          this.targetBudgetToDate[item] > 0
            ? this.data.stats[this.type][key].cost /
              this.targetBudgetToDate[item]
            : 0
      })

      data.total =
        this.targetBudgetToDate.total > 0
          ? this.actualBudget.total / this.targetBudgetToDate.total
          : 0
      return data
    },
    targetImpressions() {
      let data = {
        total: 0,
      }
      each(this.budgetKeys[this.type], (item) => {
        data[item] =
          (this.data[this.type].budgets[item] / this.data[this.type].cpm) *
          1000
      })

      data.total = this.sum(data)
      return data
    },
    targetImpressionToDate() {
      let data = {
        total: 0,
      }
      each(this.budgetKeys[this.type], (item) => {
        data[item] =
          (this.targetImpressions[item] / this.daysInPeriod) *
          this.elapsedDaysInPeriod
      })

      data.total = this.sum(data)
      return data
    },
    projectedImpressions() {
      let data = {
        total: 0,
      }
      each(this.budgetKeys[this.type], (item, key) => {
        data[item] =
          (this.data.stats[this.type][key].impressions /
            this.elapsedDaysInPeriod) *
          this.daysInPeriod
      })

      data.total = this.sum(data)
      return data
    },
    targetImpressionsSpent() {
      let data = {
        total: 0,
      }
      each(this.budgetKeys[this.type], (item, key) => {
        data[item] =
          this.targetBudget[item] > 0
            ? (this.data.stats[this.type][key].cost / this.targetBudget[item]) *
              this.targetImpressions[item]
            : 0
      })

      data.total = this.sum(data)
      return data
    },
    targetImpressionsSpentOverUnder() {
      let data = {
        total: 0,
      }
      each(this.budgetKeys[this.type], (item, key) => {
        data[item] =
          this.data.stats[this.type][key].impressions -
          this.targetImpressionsSpent[item]
      })

      data.total = this.sum(data)
      return data
    },
    percentImpressionsOverUnder() {
      let data = {
        total: 0,
      }
      each(this.budgetKeys[this.type], (item, key) => {
        data[item] =
          this.targetImpressionToDate[item] > 0
            ? this.data.stats[this.type][key].impressions /
              this.targetImpressionToDate[item]
            : 0
      })

      data.total = this.targetImpressionToDate.total > 0
            ? this.actualImpressions.total /
              this.targetImpressionToDate.total
            : 0
      return data
    },
    percentForMediaOverUnder() {
      let data = {
        total: 0,
      }
      each(this.budgetKeys[this.type], (item, key) => {
        data[item] =
          this.data.stats[this.type][key].impressions > 0 &&
          this.data[this.type].cpm > 0
            ? this.data.stats[this.type][key].cost /
              ((this.data.stats[this.type][key].impressions / 1000) *
                this.data[this.type].cpm)
            : 0
      })

      data.total =
        this.actualImpressions.total > 0 && this.data[this.type].cpm > 0
          ? this.actualBudget.total /
            ((this.actualImpressions.total / 1000) * this.data[this.type].cpm)
          : 0
      return data
    },
  },
  methods: {
    budgetHighlight(num) {
      return this.processHighlightItem(
        [
          {
            key: 1,
            operator: "gte",
            first: 85,
            second: null,
            color: "green",
          },
          {
            key: 2,
            operator: "between",
            first: 85,
            second: 70,
            color: "yellow",
          },
          {
            key: 3,
            operator: "lte",
            first: 70,
            second: null,
            color: "red",
          },
        ],
        num
      )
    },
    impressionsHighlight(num) {
      return this.processHighlightItem(
        [
          {
            key: 1,
            operator: "gte",
            first: 100,
            second: null,
            color: "green",
          },
          {
            key: 2,
            operator: "between",
            first: 100,
            second: 80,
            color: "yellow",
          },
          {
            key: 3,
            operator: "lte",
            first: 80,
            second: null,
            color: "red",
          },
        ],
        num
      )
    },
    mediaHighlight(num) {
      return this.processHighlightItem(
        [
          {
            key: 1,
            operator: "gte",
            first: 100,
            second: null,
            color: "red",
          },
          {
            key: 2,
            operator: "between",
            first: 100,
            second: 80,
            color: "yellow",
          },
          {
            key: 3,
            operator: "lte",
            first: 80,
            second: null,
            color: "green",
          },
        ],
        num
      )
    },
    sum(obj) {
      var sum = 0
      for (var el in obj) {
        if (obj.hasOwnProperty(el)) {
          sum += parseFloat(obj[el])
        }
      }
      return sum
    },
  },
}
</script>

<style lang="scss" scoped>
</style>
